import {createViewConfig} from '@sail/engine';
import {tokens} from '@sail/tokens';
import type {View} from '../view';
import {css, baseline} from '../css';
import {IconConfig} from './Icon';

export type BadgeProps = View.IntrinsicElement<
  'span',
  {
    /**
     * Content to be displayed inside the badge
     */
    children: React.ReactNode;
    /**
     * Customize the badge's color and style. Check "Choosing a type" section
     * for more details.
     */
    type:
      | 'urgent'
      | 'warning'
      | 'negative'
      | 'positive'
      | 'info'
      | 'neutral'
      | 'new';
  }
>;

export const BadgeConfig = createViewConfig({
  props: {} as BadgeProps,
  name: 'Badge',
  defaults: {
    type: 'neutral',
  },
  flattens: true,
});

/**
 * Badges are used to indicate states an item or object may move through or
 * arrive at. It is common that an item can move through many states during its
 * lifecycle.
 *
 * @see https://sail.stripe.me/components/badge/
 */
export const Badge = BadgeConfig.createView('span', {
  css: {
    borderRadius: 'xsmall',
    display: 'inline',
    font: 'label.small',
    gap: 'xsmall',
    paddingX: 'space.75',
    // Optical adjustment to ensure the default height is 20px
    paddingY: `calc(${tokens['space.xxsmall']} - 1px)`,
    stack: 'x',
    whiteSpace: 'nowrap',
    border: '1px solid',
  },

  uses: [
    baseline.css('x-middle'),
    IconConfig.customize({
      defaults: {
        size: 'xsmall',
      },
    }),
  ],

  variants: {
    type: {
      neutral: css({
        color: 'onFeedback.neutral.subdued',
      }),
      info: css({
        color: 'onFeedback.info.subdued',
      }),
      positive: css({
        color: 'onFeedback.success.subdued',
      }),
      negative: css({
        color: 'onFeedback.critical.subdued',
      }),
      warning: css({
        color: 'onFeedback.attention.subdued',
      }),
      urgent: css({
        color: 'onFeedback.critical.emphasized',
      }),
      new: css({
        color: tokens['color.brand.800'],
        fill: tokens['color.brand.800'],
        borderColor: tokens['color.brand.100'],
        backgroundColor: tokens['color.brand.50'],
      }),
    },
  },
});
